import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import {
  Alert,
  App,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import AiSalesDestinationSelect from "./AiSalesDestinationSelect";
import LeadMessages from "./LeadMessages";
import LeadOffers from "./LeadOffers";
import {
  addAiSalesLeadMessage,
  generateAiSalesLeadOffers,
  patchAiSalesLead,
  qualifyAiSalesLead,
  refreshAiSalesRecommendationMessage,
  updateAiSalesLeadStatus,
} from "../../../services/aiSalesService";
import { aiSalesStatusColor, formatAiSalesDate } from "../../../utils/aiSalesLeadUtils";

function toDayjs(d) {
  if (d == null || d === "") return undefined;
  if (dayjs.isDayjs(d)) return d;
  const x = dayjs(d);
  return x.isValid() ? x : undefined;
}

export default function LeadDetailsDrawer({ open, onClose, detail, loading, onRefresh }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [draftAiMessage, setDraftAiMessage] = useState(null);
  const [refreshMsgBusy, setRefreshMsgBusy] = useState(false);
  const { message } = App.useApp();

  const lead = detail?.lead;
  const qualification = detail?.qualification;
  const messages = detail?.messages || [];
  const offers = detail?.offers || [];
  const latestRecommendationRun = detail?.latest_recommendation_run;

  useEffect(() => {
    if (latestRecommendationRun?.message_payload_json) {
      setDraftAiMessage(null);
    }
  }, [latestRecommendationRun?.id, latestRecommendationRun?.updated_at, latestRecommendationRun?.message_payload_json]);

  const run = async (fn) => {
    if (!lead?.id) return;
    setBusy(true);
    try {
      await fn();
      await onRefresh?.();
    } catch (err) {
      const data = err?.response?.data;
      const serverMsg =
        typeof data?.message === "string" && data.message.trim()
          ? data.message.trim()
          : null;
      const fallback =
        err?.code === "ECONNABORTED"
          ? "הבקשה ארכה יותר מדי — נסו שוב."
          : err?.message && err.message !== "Request failed with status code 400"
            ? err.message
            : null;
      message.error(serverMsg || fallback || "אירעה שגיאה. נסו שוב.");
      console.error("[LeadDetailsDrawer]", err);
    } finally {
      setBusy(false);
    }
  };

  const generateOffers = useCallback(async () => {
    if (!lead?.id) return;
    setBusy(true);
    try {
      const res = await generateAiSalesLeadOffers(lead.id);
      const am = res?.payload?.ai_message;
      if (am && typeof am === "object") {
        setDraftAiMessage(am);
      }
      await onRefresh?.();
    } catch (err) {
      const data = err?.response?.data;
      const serverMsg =
        typeof data?.message === "string" && data.message.trim()
          ? data.message.trim()
          : null;
      const fallback =
        err?.code === "ECONNABORTED"
          ? "הבקשה ארכה יותר מדי — נסו שוב."
          : err?.message && err.message !== "Request failed with status code 400"
            ? err.message
            : null;
      message.error(serverMsg || fallback || "אירעה שגיאה. נסו שוב.");
      console.error("[LeadDetailsDrawer]", err);
    } finally {
      setBusy(false);
    }
  }, [lead?.id, message, onRefresh]);

  const handleRefreshCustomerMessage = useCallback(async () => {
    if (!lead?.id) return;
    setRefreshMsgBusy(true);
    try {
      const res = await refreshAiSalesRecommendationMessage(lead.id, latestRecommendationRun?.id);
      const am = res?.payload?.ai_message;
      if (am && typeof am === "object") {
        setDraftAiMessage(am);
      }
      await onRefresh?.();
      message.success("ההודעה עודכנה");
    } catch (err) {
      const data = err?.response?.data;
      const serverMsg =
        typeof data?.message === "string" && data.message.trim()
          ? data.message.trim()
          : null;
      message.error(serverMsg || err?.message || "רענון נכשל");
      console.error("[LeadDetailsDrawer] refresh message", err);
    } finally {
      setRefreshMsgBusy(false);
    }
  }, [lead?.id, latestRecommendationRun?.id, message, onRefresh]);

  useEffect(() => {
    if (!editOpen || !lead) return;
    editForm.setFieldsValue({
      customer_name: lead.customer_name ?? "",
      customer_phone: lead.customer_phone ?? "",
      customer_email: lead.customer_email ?? "",
      destination: lead.destination ?? "",
      departure_date: toDayjs(lead.departure_date),
      return_date: toDayjs(lead.return_date),
      adults_count: lead.adults_count ?? 2,
      children_count: lead.children_count ?? 0,
      infants_count: lead.infants_count ?? 0,
      budget_ils: lead.budget_ils != null ? Number(lead.budget_ils) : undefined,
      board_preference: lead.board_preference ?? "",
      notes: lead.notes ?? "",
      latest_summary: lead.latest_summary ?? "",
    });
  }, [editOpen, lead, editForm]);

  const saveLeadEdit = async () => {
    if (!lead?.id) return Promise.reject();
    let v;
    try {
      v = await editForm.validateFields();
    } catch (e) {
      if (e?.errorFields) return Promise.reject(e);
      return Promise.reject(e);
    }
    setBusy(true);
    try {
      const res = await patchAiSalesLead(lead.id, {
        customer_name: v.customer_name,
        customer_phone: v.customer_phone,
        customer_email: v.customer_email,
        destination: v.destination,
        departure_date: v.departure_date?.format("YYYY-MM-DD"),
        return_date: v.return_date?.format("YYYY-MM-DD"),
        adults_count: v.adults_count,
        children_count: v.children_count,
        infants_count: v.infants_count,
        budget_ils: v.budget_ils,
        board_preference: v.board_preference || undefined,
        notes: v.notes || undefined,
        latest_summary: v.latest_summary || undefined,
      });
      if (res.status === "ok") {
        message.success("הליד עודכן");
        setEditOpen(false);
        await onRefresh?.();
      } else {
        message.error(res.message || "עדכון נכשל");
        return Promise.reject(new Error(res.message || "fail"));
      }
    } catch (e) {
      const data = e?.response?.data;
      const serverMsg =
        typeof data?.message === "string" && data.message.trim() ? data.message.trim() : null;
      message.error(serverMsg || e?.message || "עדכון נכשל");
      return Promise.reject(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title="פרטי ליד"
      width={720}
      placement="left"
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {loading || !lead ? (
        <Spin />
      ) : (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color={aiSalesStatusColor(lead.status)}>{lead.status}</Tag>
            {qualification?.is_ready_for_offer && <Tag color="green">מוכן להצעות</Tag>}
          </Space>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="שם">{lead.customer_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="טלפון">{lead.customer_phone || "—"}</Descriptions.Item>
            <Descriptions.Item label="אימייל">{lead.customer_email || "—"}</Descriptions.Item>
            <Descriptions.Item label="יעד">{lead.destination || "—"}</Descriptions.Item>
            <Descriptions.Item label="תאריכים">
              {formatAiSalesDate(lead.departure_date)} — {formatAiSalesDate(lead.return_date)}
            </Descriptions.Item>
            <Descriptions.Item label="מבוגרים / ילדים / תינוקות">
              {[lead.adults_count, lead.children_count, lead.infants_count].map((x) => x ?? "—").join(" / ")}
            </Descriptions.Item>
            <Descriptions.Item label="תקציב ₪">{lead.budget_ils ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="סיכום">{lead.latest_summary || "—"}</Descriptions.Item>
          </Descriptions>

          {qualification && !qualification.is_ready_for_offer && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              message="חסרים שדות"
              description={
                <>
                  <div>{(qualification.missing_fields || []).join(", ")}</div>
                  {qualification.next_question && (
                    <Typography.Paragraph strong style={{ marginTop: 8 }}>
                      שאלה הבאה: {qualification.next_question}
                    </Typography.Paragraph>
                  )}
                </>
              }
            />
          )}

          <Space wrap style={{ margin: "16px 0" }}>
            <Button onClick={() => setEditOpen(true)}>ערוך ליד</Button>
            <Button
              onClick={() => run(() => qualifyAiSalesLead(lead.id))}
              loading={busy}
            >
              סטטוס התאמה
            </Button>
            <Button
              type="primary"
              disabled={!qualification?.is_ready_for_offer}
              loading={busy}
              onClick={generateOffers}
            >
              צור הצעות (עד 3)
            </Button>
            <Button loading={busy} onClick={() => run(() => updateAiSalesLeadStatus(lead.id, "hot"))}>
              סמן חם
            </Button>
            <Button loading={busy} onClick={() => run(() => updateAiSalesLeadStatus(lead.id, "closed"))}>
              סגור
            </Button>
            <Button loading={busy} danger onClick={() => run(() => updateAiSalesLeadStatus(lead.id, "lost"))}>
              אבוד
            </Button>
          </Space>

          <Typography.Title level={5}>הודעות</Typography.Title>
          <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
            <Input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="הודעת סוכן..."
            />
            <Button
              type="primary"
              loading={busy}
              onClick={() =>
                run(async () => {
                  await addAiSalesLeadMessage(lead.id, msg, "agent");
                  setMsg("");
                })
              }
            >
              שלח
            </Button>
          </Space.Compact>
          <LeadMessages messages={messages} />

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            הצעות
          </Typography.Title>
          <LeadOffers
            offers={offers}
            leadId={lead.id}
            customerPhone={lead.customer_phone}
            recommendationRun={latestRecommendationRun}
            draftAiMessage={draftAiMessage}
            onRefreshMessage={latestRecommendationRun?.id ? handleRefreshCustomerMessage : undefined}
            refreshMessageLoading={refreshMsgBusy}
          />

          <Modal
            title="עריכת ליד"
            open={editOpen}
            onCancel={() => setEditOpen(false)}
            onOk={saveLeadEdit}
            confirmLoading={busy}
            okText="שמור"
            cancelText="ביטול"
            width={560}
            destroyOnHidden
          >
            <Form form={editForm} layout="vertical" style={{ marginTop: 8 }}>
              <Form.Item name="customer_name" label="שם">
                <Input />
              </Form.Item>
              <Form.Item name="customer_phone" label="טלפון">
                <Input />
              </Form.Item>
              <Form.Item name="customer_email" label="אימייל">
                <Input type="email" />
              </Form.Item>
              <Form.Item
                name="destination"
                label="יעד (חבילות LDS)"
                rules={[{ required: true, message: "חובה" }]}
              >
                <AiSalesDestinationSelect />
              </Form.Item>
              <Space wrap style={{ width: "100%" }}>
                <Form.Item
                  name="departure_date"
                  label="יציאה"
                  rules={[{ required: true, message: "חובה" }]}
                >
                  <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  name="return_date"
                  label="חזרה"
                  rules={[{ required: true, message: "חובה" }]}
                >
                  <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
                </Form.Item>
              </Space>
              <Space wrap>
                <Form.Item name="adults_count" label="מבוגרים" rules={[{ required: true }]}>
                  <InputNumber min={1} />
                </Form.Item>
                <Form.Item name="children_count" label="ילדים">
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item name="infants_count" label="תינוקות">
                  <InputNumber min={0} />
                </Form.Item>
              </Space>
              <Form.Item name="budget_ils" label="תקציב ₪">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="board_preference" label="תזונה / לינה (אופציונלי)">
                <Input />
              </Form.Item>
              <Form.Item name="notes" label="הערות">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item name="latest_summary" label="סיכום">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}
    </Drawer>
  );
}
